"use client";

import { useState } from "react";
import { Search, Grid3x3, List, SearchX } from "lucide-react";
import type { Category } from "@/types";
import Card from "@/components/ui/Card";
import CardCategory from "@/components/ui/CardCategory";

type CategoriesGridProps = {
  categories: Category[];
};

type ViewMode = "grid" | "list";
type SortMode = "name" | "count" | "popular";

export default function CategoriesGrid({ categories }: CategoriesGridProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortMode, setSortMode] = useState<SortMode>("name");
  const [searchQuery, setSearchQuery] = useState("");

  // Filtrar por busca
  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Ordenar
  const sortedCategories = [...filteredCategories].sort((a, b) => {
    switch (sortMode) {
      case "name":
        return a.name.localeCompare(b.name);
      case "count":
        return (b.qtd || 0) - (a.qtd || 0);
      case "popular":
        // Por enquanto usa qtd, mas pode ser substituído por views/popularidade
        return (b.qtd || 0) - (a.qtd || 0);
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-6">
      {/* Barra de Controles - Desktop */}
      <div className="hidden md:flex items-center justify-between gap-4 bg-white p-4 rounded-lg border border-gray-200">
        {/* Busca */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar categoria..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mygreen focus:border-transparent"
            />
          </div>
        </div>

        {/* Controles */}
        <div className="flex items-center gap-4">
          {/* Ordenação */}
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as SortMode)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mygreen focus:border-transparent"
          >
            <option value="name">A-Z</option>
            <option value="count">Mais Empresas</option>
            <option value="popular">Mais Popular</option>
          </select>

          {/* View Mode */}
          <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded transition-colors ${
                viewMode === "grid"
                  ? "bg-mygreen text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              title="Visualização em grade"
            >
              <Grid3x3 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-1.5 rounded transition-colors ${
                viewMode === "list"
                  ? "bg-mygreen text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              title="Visualização em lista"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Contador de Resultados */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {filteredCategories.length === categories.length ? (
            <>
              <span className="font-semibold">{categories.length}</span>{" "}
              {categories.length === 1 ? "categoria" : "categorias"}
            </>
          ) : (
            <>
              <span className="font-semibold">{filteredCategories.length}</span>{" "}
              de <span className="font-semibold">{categories.length}</span>{" "}
              categorias
            </>
          )}
        </p>
      </div>

      {/* Grid/List de Categorias */}
      {viewMode === "grid" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {sortedCategories.map((category) => (
            <CardCategory key={category.id} category={category} />
          ))}
          {/* Card Serviços */}
          <CardCategory
            category={{
              id: 0,
              name: "Serviços",
              slug: "servicos",
              url: "/servicos",
              image: null,
              description: "Prestadores de serviços",
              qtd: 0,
            }}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {sortedCategories.map((category) => (
            <Card
              key={category.id}
              variant="horizontal"
              name={category.name}
              description={
                category.description ||
                `${category.qtd || 0} empresas cadastradas`
              }
              image={category.image}
              href={`/categoria/${category.slug}`}
            />
          ))}
          {/* Card Serviços */}
          <Card
            variant="horizontal"
            name="Serviços"
            description="Prestadores de serviços"
            image={null}
            href="/servicos"
          />
        </div>
      )}

      {/* Empty State */}
      {filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <SearchX className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Nenhuma categoria encontrada
          </h3>
          <p className="text-gray-600">Tente buscar por outro termo</p>
        </div>
      )}
    </div>
  );
}
